import { Box, Heading, HStack, SimpleGrid, Text, VStack } from '@chakra-ui/react'
import { LegiScanCredit } from '../LegiScanCredit'
import type { RollCallFillResult } from '../../../types/lobbyDay'

// LegiScan roll-call ChamberId: 1 = lower/House, 2 = upper/Senate (see LegiScanRollCallFillService).
const CHAMBER_LABEL: Record<number, string> = { 1: 'House', 2: 'Senate' }

// The fill's coded miss reasons (RollCallSelection) are never shown raw — humanize them here and in
// the vote-row hint (AskReconciliationSection maps rows through this same table).
export const MISS_REASON_LABEL: Record<string, string> = {
  NotYetVoted: 'no floor vote recorded yet',
  NoFloorVoteInChamber: 'no linked bill for their chamber',
  NotInRoster: 'not in roster (manual legislator — sync to enable)',
}

export function missReasonLabel(reason: string): string {
  return MISS_REASON_LABEL[reason] ?? reason
}

function formatRollCallDate(iso: string | null): string | null {
  if (!iso) return null
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return null
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
}

interface CountTileProps {
  n: number
  label: string
  color: string
}

function CountTile({ n, label, color }: CountTileProps) {
  return (
    <Box borderWidth="1px" borderColor="border" borderRadius="md" bg="surface.2" p={3} textAlign="center">
      <Text fontSize="2xl" fontWeight="bold" lineHeight="1" color={color}>{n}</Text>
      <Text fontSize="xs" color="muted" textTransform="uppercase" letterSpacing="wide" mt={1}>{label}</Text>
    </Box>
  )
}

export interface FillResultSummaryProps {
  result: RollCallFillResult
  askTitle?: string
}

export function FillResultSummary({ result, askTitle }: FillResultSummaryProps) {
  const noVoteCount = result.noVoteFound.length

  return (
    <VStack align="stretch" gap={4}>
      <Box>
        <Heading size="sm">Votes filled from LegiScan</Heading>
        {!!askTitle && <Text fontSize="xs" color="muted" mt={1}>{askTitle}</Text>}
      </Box>

      <SimpleGrid columns={3} gap={3}>
        <CountTile n={result.filled} label="Filled" color="success.fg" />
        <CountTile n={result.keptManual} label="Kept manual" color="ink" />
        <CountTile n={noVoteCount} label="No vote found" color="warning.fg" />
      </SimpleGrid>

      {result.rollCallsUsed.length > 0 && (
        <Box>
          <Text fontSize="xs" color="muted" textTransform="uppercase" letterSpacing="wide" mb={2}>Roll calls used</Text>
          <VStack align="stretch" gap={2}>
            {result.rollCallsUsed.map((rc, i) => {
              const date = formatRollCallDate(rc.date)
              const meta = [CHAMBER_LABEL[rc.chamberId], date].filter(Boolean).join(' · ')
              return (
                <HStack key={`${rc.billNumber}-${i}`} borderWidth="1px" borderColor="border" borderRadius="md"
                  bg="surface.2" px={3} py={2} gap={2} flexWrap="wrap">
                  <Text fontWeight="bold" fontSize="sm">{rc.billNumber}</Text>
                  {!!meta && <Text fontSize="sm" color="muted">{meta}</Text>}
                  {rc.skippedUnchanged && (
                    <Text fontSize="xs" color="muted" fontStyle="italic" ml="auto">unchanged since last fill — skipped</Text>
                  )}
                </HStack>
              )
            })}
          </VStack>
        </Box>
      )}

      {noVoteCount > 0 && (
        <Box>
          <Text fontSize="xs" color="muted" textTransform="uppercase" letterSpacing="wide" mb={2}>
            Couldn't resolve ({noVoteCount})
          </Text>
          <VStack align="stretch" gap={1}>
            {result.noVoteFound.map((miss, i) => (
              <HStack key={`${miss.legislatorName}-${i}`} align="baseline" gap={2} flexWrap="wrap">
                <Text fontSize="sm" fontWeight="semibold">{miss.legislatorName}</Text>
                <Text fontSize="sm" color="warning.fg">{missReasonLabel(miss.reason)}</Text>
              </HStack>
            ))}
          </VStack>
        </Box>
      )}

      <LegiScanCredit />
    </VStack>
  )
}
